import React, { useCallback, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartOptions, Plugin } from 'chart.js';
import { useParams } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Define a plugin to adjust bar heights
const minHeightPlugin: Plugin<'bar'> = {
  id: 'minHeight',
  afterDatasetDraw: (chart, args, options) => {
    const { ctx, chartArea: { bottom }, scales: { y } } = chart;
    chart.getDatasetMeta(0).data.forEach((bar: any, index: number) => {
      const value = chart.data.datasets[0].data[index] as number;
      if (value > 0 && value < 6) {
        const minBarHeight = 5; // Minimum bar height in pixels
        const barHeight = Math.max(minBarHeight, y.getPixelForValue(value) - y.getPixelForValue(0));
        ctx.fillStyle = bar.options.backgroundColor as string;
        const yPos = bottom - barHeight;
        ctx.fillRect(bar.x - bar.width / 2, yPos, bar.width, barHeight);
      }
    });
  }
};

// Register the plugin
ChartJS.register(minHeightPlugin);

const WorkPerformanceGraph: React.FC = () => {
  const [monthlySummary, setMonthlySummary] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation('driver');

  const currentYear = new Date().getFullYear();

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = `/v1/attendances/summarize_monthly/?check_in__gte=${currentYear}-01-01&check_in__lte=${currentYear}-12-31&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      setMonthlySummary(response.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [id, currentYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const monthlyData = Array(12).fill(0);
  monthlySummary.forEach((summary) => {
    const month = new Date(summary.month).getMonth();
    monthlyData[month] = parseFloat(summary.total_duration_hours.toFixed(2));
  });

  const data = {
    labels: [
      `${t('view.driver-detail.work-performance.Jan')}`, 
      `${t('view.driver-detail.work-performance.Feb')}`, 
      `${t('view.driver-detail.work-performance.Mar')}`, 
      `${t('view.driver-detail.work-performance.Apr')}`, 
      `${t('view.driver-detail.work-performance.May')}`, 
      `${t('view.driver-detail.work-performance.Jun')}`, 
      `${t('view.driver-detail.work-performance.Jul')}`, 
      `${t('view.driver-detail.work-performance.Aug')}`, 
      `${t('view.driver-detail.work-performance.Sep')}`, 
      `${t('view.driver-detail.work-performance.Oct')}`, 
      `${t('view.driver-detail.work-performance.Nov')}`, 
      `${t('view.driver-detail.work-performance.Dec')}`],
    datasets: [
      {
        label: `${t('view.driver-detail.work-performance.working-hrs')}`,
        data: monthlyData,
        backgroundColor: '#589EF6',
        borderColor: '#589EF6',
        borderWidth: 1,
        borderRadius: 5,
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(tickValue: string | number) {
            return `${Number(tickValue)} ${t('view.driver-detail.work-performance.hrs')}`;
          },
        },
        grid: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {},
    },
  };

  return <Bar data={data} options={options} />;
};

export default WorkPerformanceGraph;